import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '../..';
import parseStatusCode from '../../errors/parseStatusCode';
import type { Injections } from '../../services/injections';
import type { Contract } from '../../services/FP_FulfillmentContextService/types';
import type { ApplicationErrorOptions } from '../../../types';
import type {
  BulkAssetLinkResults,
  BulkLinkContractToContextsPayload,
} from '../../services/UIOrchestrationService/types';

export interface ContractsModel {
  contractsMap: Record<string, Contract>;
  setContracts: Action<ContractsModel, Contract[]>;
  setContract: Action<ContractsModel, Contract>;
  fetchContractsLoading: boolean;
  setFetchContractsLoading: Action<ContractsModel, boolean>;
  fetchContracts: Thunk<ContractsModel, void, Injections, StoreModel, Promise<void>>;
  fetchContractsFailure: Action<ContractsModel, ApplicationErrorOptions>;
  bulkLinkContractToContext: Thunk<
    ContractsModel,
    BulkLinkContractToContextsPayload,
    Injections,
    StoreModel,
    Promise<BulkAssetLinkResults>
  >;
}

export const contractsModel: ContractsModel = {
  contractsMap: {},
  setContracts: action((state, contracts) => {
    const contractsMap = contracts.reduce<Record<string, Contract>>((acc, curr) => {
      acc[curr.contractId] = curr;
      return acc;
    }, {});
    state.contractsMap = contractsMap;
  }),
  setContract: action((state, contract) => {
    state.contractsMap[contract.contractId] = contract;
  }),
  fetchContractsLoading: false,
  setFetchContractsLoading: action((state, isLoading) => {
    state.fetchContractsLoading = isLoading;
  }),
  fetchContracts: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchContractsLoading(true);
      const { fulfillmentContextService } = injections;
      const response = await fulfillmentContextService.fetchContracts();
      actions.setContracts(response);
      actions.setFetchContractsLoading(false);
      return response;
    } catch (error) {
      actions.fetchContractsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      actions.setFetchContractsLoading(false);
      return error;
    }
  }),
  fetchContractsFailure: action((state, error) => {
    console.error(error);
  }),
  bulkLinkContractToContext: thunk(async (actions, payload, { injections, getState }) => {
    try {
      const { UIOrchestrationService } = injections;
      const response = await UIOrchestrationService.bulkLinkContractToContexts(payload);
      return response;
    } catch (error) {
      actions.fetchContractsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      throw error;
    }
  }),
};

export default contractsModel;
